import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Save } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const toList = (s) => s.split(',').map(x => x.trim()).filter(Boolean);

export function ProcurementApproverGroupCard() {
  const { user } = useAuth() || {};
  const isSA = (user?.role || '').toLowerCase() === 'super_admin';
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/settings/procurement-approver-group').then(d => setCfg({
      ...d, depText: (d.departments || []).join(', '), itText: (d.it_departments || []).join(', '),
    })).catch(() => {});
  }, []);
  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/settings/procurement-approver-group', {
        departments: toList(cfg.depText), it_departments: toList(cfg.itText),
        include_it: !!cfg.include_it, include_senior: !!cfg.include_senior,
      });
      toast.success('Procurement approver group saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const inputCls = 'w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-[#3b4872] focus:outline-none disabled:bg-slate-50';
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 mt-6" data-testid="procurement-approver-group-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={15} className="text-[#3b4872]" />
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#3b4872]">Procurement approver group</div>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Members may approve MPRs, RFQ justifications, POs, Work Orders and vendor empanelment — including items they created — at any value.
        Super admin is always included. Self-approvals are recorded in the audit log.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600 block mb-1">Procurement departments (comma separated)</span>
          <input disabled={!isSA} value={cfg.depText} onChange={e => setCfg({ ...cfg, depText: e.target.value })} className={inputCls} data-testid="pag-departments" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600 block mb-1">IT departments (comma separated)</span>
          <input disabled={!isSA || !cfg.include_it} value={cfg.itText} onChange={e => setCfg({ ...cfg, itText: e.target.value })} className={inputCls} data-testid="pag-it-departments" />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" disabled={!isSA} checked={!!cfg.include_it} onChange={e => setCfg({ ...cfg, include_it: e.target.checked })} data-testid="pag-include-it" />
          Include the IT team
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" disabled={!isSA} checked={!!cfg.include_senior} onChange={e => setCfg({ ...cfg, include_senior: e.target.checked })} data-testid="pag-include-senior" />
          Include Senior Management (MD / CEO / CMD / CFO and senior profiles)
        </label>
      </div>
      {isSA ? (
        <div className="flex justify-end mt-3">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-50" data-testid="pag-save-btn">
            <Save size={14} /> {saving ? 'Saving…' : 'Save group'}
          </button>
        </div>
      ) : <p className="text-[11px] text-slate-400 mt-3">Only a super admin can change this group.</p>}
    </div>
  );
}
